import { ItemContext } from '#UNSAFE_Collection';
import { LoadMoreCollection } from '#PRIVATE_LoadMoreCollection';
import { TreeItem } from './TreeItem';
import { InternalTreeNode, LoadMoreTreeCollectionProps as Props } from './TreeView.types';
import { SkeletonContainer } from './SkeletonContainer';
import { TreeItemSkeletons } from './TreeItemSkeletons';
import { LOADMORE_STYLE_CLASS } from '#PRIVATE_LoadMoreCollection';
// TODO: add this import due to issue JET-67485, need to update once Stable is introduced
import './themes/redwood/TreeViewTheme.css';

/**
 * Component that receives the flattened data and renders the hierarchy betweens items based on the shaped metadata.
 * In order to maximize performance, only items that are visible in the viewport are rendered.
 * This is an internal component that is used by FlatTreeView.
 */
export function LoadMoreTreeCollection<K extends string | number, D>({
  data,
  children,
  selectionMode,
  onToggle = () => {},
  viewportConfig,
  hasMore = false,
  onLoadMore = () => {},
  loadMoreIndicator = defaultLoadMoreIndicator,
  updateContext
}: Props<K, D>) {
  const createTreeItem = (context: ItemContext<InternalTreeNode<K, D>>) => {
    const key = context.data.metadata.key;
    const treeItemContext = {
      index: context.index,
      data: context.data.data,
      metadata: { key }
    };
    // allow container to inject additional things to context
    if (updateContext) {
      updateContext(treeItemContext);
    }
    const expanded = context.data.metadata.expanded;
    return (
      <TreeItem
        key={treeItemContext.metadata.key}
        itemKey={treeItemContext.metadata.key}
        context={treeItemContext}
        depth={context.data.metadata.treeDepth}
        isExpanded={expanded === 'ongoing' ? true : expanded}
        onToggle={onToggle}
        isSelectable={selectionMode !== 'none'}
        isCheckboxless={selectionMode === 'single'} // TODO: need to update when checkboxless multiple is added
        selected={context.data.metadata.selected}
        isLoading={expanded === 'ongoing'}
        isTabbable={false}
        isFocusRingVisible={context.data.metadata.hasFocus}>
        {children}
      </TreeItem>
    );
  };

  return (
    <>
      {data === null ? null : (
        <LoadMoreCollection
          data={data}
          viewportConfig={viewportConfig}
          hasMore={hasMore}
          onLoadMore={onLoadMore}
          loadMoreIndicator={loadMoreIndicator}>
          {createTreeItem}
        </LoadMoreCollection>
      )}
    </>
  );
}

const defaultLoadMoreIndicator = (
  <div class={LOADMORE_STYLE_CLASS}>
    <SkeletonContainer minimumCount={1}>
      {() => {
        return <TreeItemSkeletons />;
      }}
    </SkeletonContainer>
  </div>
);
